import React from 'react';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

/**
 * Layout skeleton for the sensor details page (back, title+toggle, description,
 * remediation, details fields).
 * Used as Suspense fallback after the page chunk mounts, and as the route.skeleton
 * body (via spa-apps SensorDetailsPageSkeleton).
 */
const TEST_ID_PREFIX = 'sensor-details-skeleton';

const styles = cssMap({
	contentWrapper: {
		maxInlineSize: '67.5rem',
	},
	panelSkeleton: {
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
	},
});

const SectionHeadingSkeleton = ({ width }: { width: string }): React.JSX.Element => (
	<Skeleton width={width} height="20px" borderRadius={token('radius.small')} />
);

const BodyLinesSkeleton = (): React.JSX.Element => (
	<Stack space="space.100">
		<Skeleton width="100%" height="16px" borderRadius={token('radius.small')} />
		<Skeleton width="95%" height="16px" borderRadius={token('radius.small')} />
		<Skeleton width="70%" height="16px" borderRadius={token('radius.small')} />
	</Stack>
);

const DetailFieldSkeleton = ({
	labelWidth,
	valueWidth,
}: {
	labelWidth: string;
	valueWidth: string;
}): React.JSX.Element => (
	<Stack space="space.050">
		<Skeleton width={labelWidth} height="14px" borderRadius={token('radius.small')} />
		<Skeleton width={valueWidth} height="16px" borderRadius={token('radius.small')} />
	</Stack>
);

export const PanelSkeleton = (): React.JSX.Element => (
	<Box aria-hidden="true" testId={TEST_ID_PREFIX} xcss={styles.panelSkeleton}>
		<Stack space="space.200">
			<Skeleton
				testId={`${TEST_ID_PREFIX}-back`}
				width="158px"
				height="32px"
				borderRadius={token('radius.small')}
			/>

			<Inline alignBlock="center" spread="space-between" space="space.200">
				<Skeleton
					testId={`${TEST_ID_PREFIX}-title`}
					width="45%"
					height="32px"
					borderRadius={token('radius.small')}
				/>
				<Inline alignBlock="center" space="space.200">
					<Skeleton width="72px" height="16px" borderRadius={token('radius.small')} />
					<Skeleton width="40px" height="20px" borderRadius={token('radius.small')} />
				</Inline>
			</Inline>

			<Box xcss={styles.contentWrapper}>
				<Stack space="space.400">
					<Stack space="space.150">
						<SectionHeadingSkeleton width="110px" />
						<BodyLinesSkeleton />
					</Stack>

					<Stack space="space.150">
						<SectionHeadingSkeleton width="150px" />
						<BodyLinesSkeleton />
					</Stack>

					<Stack space="space.150">
						<SectionHeadingSkeleton width="80px" />
						<Stack space="space.200">
							<DetailFieldSkeleton labelWidth="80px" valueWidth="120px" />
							<DetailFieldSkeleton labelWidth="130px" valueWidth="140px" />
							<DetailFieldSkeleton labelWidth="160px" valueWidth="200px" />
							<DetailFieldSkeleton labelWidth="130px" valueWidth="140px" />
							<DetailFieldSkeleton labelWidth="160px" valueWidth="200px" />
						</Stack>
					</Stack>
				</Stack>
			</Box>
		</Stack>
	</Box>
);
